import { breakpointValues, hookBreakpoints } from "theme/breakpoints";
import { useMediaQuery } from "@chakra-ui/react";
import type { Devices } from "utils/types";
import { useEffect, useState } from "react";

type Breakpoints = {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
};

const REM = 16;
export const breakpointsInPx: Breakpoints = Object.keys(
  breakpointValues
).reduce(
  (acc, key) => {
    acc[key] = breakpointValues[key].replace("rem", "") * REM;
    return acc;
  },
  {
    xs: 0,
    sm: 0,
    md: 0,
    lg: 0,
    xl: 0
  }
);

// TODO: move to packages
export const getWindowWidth = (): number => {
  if (typeof window === "undefined") return 0;

  const documentClientWidth =
    typeof document !== "undefined"
      ? document.documentElement.clientWidth
      : window.innerWidth;

  const bodyClientWidth =
    typeof document !== "undefined"
      ? document.getElementsByTagName("body")[0].clientWidth
      : 0;

  const width =
    window.innerWidth && documentClientWidth
      ? Math.min(window.innerWidth, documentClientWidth)
      : window.innerWidth || documentClientWidth || bodyClientWidth || 0;

  if (typeof width !== "number") return 0;
  return width;
};

// @todo(matt d): desktop is 1280 so breakpoints are no longer correct. investigate best way to tidy up
export const checkViewport = (): Devices => {
  const windowWidth = getWindowWidth();

  if (windowWidth <= breakpointsInPx.md) {
    return "mobile";
  }
  if (windowWidth <= breakpointsInPx.lg) {
    return "tablet";
  }
  if (windowWidth >= breakpointsInPx.lg) {
    return "desktop";
  }

  // defaulting to mobile.
  return "mobile";
};

export interface UseScreenFromBreakpoints {
  isTiny: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isExtraLarge: boolean;
}

export const useScreenFromBreakpoints = (): UseScreenFromBreakpoints => {
  const [isTiny, isMobile, isTablet, isDesktop, isExtraLarge] = useMediaQuery([
    `(max-width: 374px)`,
    hookBreakpoints.mobileMax,
    hookBreakpoints.tablet,
    hookBreakpoints.desktop,
    hookBreakpoints.extraLarge
  ]);

  return { isTiny, isMobile, isTablet, isDesktop, isExtraLarge };
};

export const useScreenViewport = (): UseScreenFromBreakpoints => {
  const [viewport, setViewport] = useState({
    isTiny: false,
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isExtraLarge: false
  });

  const getViewports = (): UseScreenFromBreakpoints => {
    const windowWidth = getWindowWidth();
    return {
      isTiny: windowWidth <= 374, // Below iPhone 6 (excluded)
      isMobile: windowWidth <= breakpointsInPx.md,
      isTablet:
        windowWidth > breakpointsInPx.md && windowWidth <= breakpointsInPx.lg,
      isDesktop:
        windowWidth > breakpointsInPx.lg && windowWidth <= breakpointsInPx.xl,
      isExtraLarge: windowWidth > breakpointsInPx.xl
    };
  };

  useEffect(() => {
    let timeout: null | number = null;
    const resizeListener = () => {
      if (typeof timeout === "number") clearTimeout(timeout);
      timeout = global.window.setTimeout(
        () => setViewport(getViewports()),
        500
      );
    };

    setViewport(getViewports());
    window.addEventListener("resize", resizeListener);
    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  return viewport;
};
